import React, { useState, useEffect } from 'react';
import { FiCalendar, FiClock, FiCheck, FiX, FiPlus, FiTrash2, FiUser } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { getAuthToken } from '../services/api';
import { API_URL } from '../config';
import axios from 'axios';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

function TimeOff() {
  const { user } = useAuth();
  const [balances, setBalances] = useState(null);
  const [requests, setRequests] = useState([]);
  const [allBalances, setAllBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [activeTab, setActiveTab] = useState('my-time');
  
  // Form state
  const [requestType, setRequestType] = useState('Vacation');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hours, setHours] = useState('');
  const [notes, setNotes] = useState('');
  const [paidDays, setPaidDays] = useState(null); // null = all paid
  const [workdays, setWorkdays] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const isManager = ['Admin', 'Owner', 'Office Manager'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const config = await getAuthConfig();
      
      // Load my balances
      const balanceRes = await axios.get(`${API_URL}/time-off/balances`, config);
      setBalances(balanceRes.data);
      
      // Load requests
      const requestsRes = await axios.get(`${API_URL}/time-off/requests`, config);
      setRequests(requestsRes.data.requests || []);
      
      // Load all balances if manager
      if (isManager) {
        const allRes = await axios.get(`${API_URL}/time-off/all-balances`, config);
        setAllBalances(allRes.data.employees || []);
      }
    } catch (error) {
      console.error('Error loading time off data:', error);
    }
    setLoading(false);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-off/requests`, {
        request_type: requestType,
        start_date: startDate,
        end_date: endDate,
        hours: parseFloat(hours),
        paid_days: requestType === 'Unpaid' ? 0 : paidDays,
        notes
      }, config);

      setShowRequestForm(false);
      setRequestType('Vacation');
      setStartDate('');
      setEndDate('');
      setHours('');
      setNotes('');
      setPaidDays(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to submit request');
    }
    setSubmitting(false);
  };

  const handleApprove = async (requestId) => {
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-off/requests/${requestId}/approve`, {}, config);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to approve request');
    }
  };

  const handleDeny = async (requestId) => {
    const reason = prompt('Reason for denial (optional):');
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-off/requests/${requestId}/deny`, { reason }, config);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to deny request');
    }
  };

  const handleDelete = async (requestId, status) => {
    const message = status === 'Approved'
      ? 'Delete this request? This will also remove the associated payroll entries.'
      : 'Delete this request?';
    if (!window.confirm(message)) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/time-off/requests/${requestId}`, config);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete request');
    }
  };

  const calculateHoursFromDates = () => {
    if (startDate && endDate) {
      // Parse dates as local dates (not UTC) to avoid timezone issues
      const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);

      let days = 0;
      const current = new Date(start);

      while (current <= end) {
        const dayOfWeek = current.getDay();
        // Count Mon-Thu only (0 = Sunday, 6 = Saturday)
        if (dayOfWeek >= 1 && dayOfWeek <= 4) {
          days++;
        }
        current.setDate(current.getDate() + 1);
      }

      setWorkdays(days);
      setHours((days * 10).toString()); // 10 hours per day
      setPaidDays(days); // Default: all days paid
    } else {
      setWorkdays(0);
      setPaidDays(null);
    }
  };

  useEffect(() => {
    calculateHoursFromDates();
  }, [startDate, endDate]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return '#22c55e';
      case 'Denied': return '#ef4444';
      default: return '#f59e0b';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // For date-only strings (YYYY-MM-DD), parse as local to avoid timezone shift
    let d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, day] = dateStr.split('-').map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(dateStr);
    }
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const BalanceCard = ({ title, data, color }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      borderLeft: `4px solid ${color}`
    }}>
      <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>{title}</div>
      <div style={{ fontSize: '32px', fontWeight: '700', color }}>
        {data.available}
        <span style={{ fontSize: '14px', color: '#999', fontWeight: '400' }}> days available</span>
      </div>
      <div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
        {data.entitled} entitled • {data.used} used
        {data.carryover > 0 && ` • ${data.carryover} carried over`}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading time off data...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '25px'
      }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiCalendar /> Time Off
          </h1>
          <p style={{ color: '#666', margin: '5px 0 0' }}>
            Request and manage time off
          </p>
        </div>
        <button
          onClick={() => setShowRequestForm(true)}
          style={{
            padding: '12px 20px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <FiPlus /> Request Time Off
        </button>
      </div>

      {/* Tabs for managers */}
      {isManager && (
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '20px',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '10px'
        }}>
          <button
            onClick={() => setActiveTab('my-time')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === 'my-time' ? '#2563eb' : 'transparent',
              color: activeTab === 'my-time' ? 'white' : '#666',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            My Time Off
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === 'pending' ? '#2563eb' : 'transparent',
              color: activeTab === 'pending' ? 'white' : '#666',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Pending Requests
            {requests.filter(r => r.status === 'Pending').length > 0 && (
              <span style={{
                marginLeft: '6px',
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px'
              }}>
                {requests.filter(r => r.status === 'Pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('all-balances')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === 'all-balances' ? '#2563eb' : 'transparent',
              color: activeTab === 'all-balances' ? 'white' : '#666',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            All Employees
          </button>
        </div>
      )}

      {/* My Time Off Tab */}
      {activeTab === 'my-time' && (
        <>
          {/* Balances */}
          {balances ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <BalanceCard 
                title="Vacation" 
                data={balances.vacation} 
                color="#2563eb" 
              />
              <BalanceCard 
                title="Sick Leave" 
                data={{ 
                  ...balances.sick, 
                  entitled: balances.sick.total 
                }} 
                color="#22c55e" 
              />
              <BalanceCard 
                title="Personal" 
                data={balances.personal} 
                color="#8b5cf6" 
              />
            </div>
          ) : (
            <div style={{
              backgroundColor: '#fef3c7',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#92400e'
            }}>
              <strong>Note:</strong> Your hire date has not been set. Please contact your manager to set up your time off benefits.
            </div>
          )}

          {/* Employment Info */}
          {balances && (
            <div style={{
              backgroundColor: '#f8fafc',
              padding: '15px 20px',
              borderRadius: '8px',
              marginBottom: '25px',
              fontSize: '14px',
              color: '#64748b'
            }}>
              <FiUser style={{ marginRight: '8px' }} />
              <strong>{balances.employment_type}</strong> • 
              Hire Date: {formatDate(balances.hire_date)} • 
              Years Employed: {balances.years_employed}
            </div>
          )}

          {/* My Requests */}
          <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>My Requests</h2>
          
          {requests.filter(r => r.team_member_id === user?.id).length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              color: '#666'
            }}>
              No time off requests yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {requests.filter(r => r.team_member_id === user?.id).map(request => (
                <div
                  key={request.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    padding: '16px 20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {request.request_type}
                      {request.paid_days != null && request.paid_days < Math.round(request.hours / 10) && (
                        <span style={{ fontSize: '12px', fontWeight: '400', color: '#f59e0b', marginLeft: '8px' }}>
                          ({request.paid_days} paid, {Math.round(request.hours / 10) - request.paid_days} unpaid)
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      {formatDate(request.start_date)}
                      {request.start_date !== request.end_date && ` - ${formatDate(request.end_date)}`}
                      {' • '}{request.hours} hours
                    </div>
                    {request.notes && (
                      <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                        {request.notes}
                      </div>
                    )}
                    {request.denial_reason && (
                      <div style={{ fontSize: '13px', color: '#ef4444', marginTop: '4px' }}>
                        Reason: {request.denial_reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: `${getStatusColor(request.status)}15`,
                      color: getStatusColor(request.status)
                    }}>
                      {request.status}
                    </span>
                    {(request.status === 'Pending' || isManager) && (
                      <button
                        onClick={() => handleDelete(request.id, request.status)}
                        style={{
                          padding: '8px',
                          backgroundColor: '#fee2e2',
                          color: '#ef4444',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                        title={request.status === 'Approved' ? 'Delete request and remove payroll entries' : 'Delete request'}
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Pending Requests Tab (Managers) */}
      {activeTab === 'pending' && isManager && (
        <>
          <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>
            Pending Requests
          </h2>
          
          {requests.filter(r => r.status === 'Pending').length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              color: '#666'
            }}>
              No pending requests
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {requests.filter(r => r.status === 'Pending').map(request => (
                <div
                  key={request.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    padding: '16px 20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {request.team_member_name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      {request.request_type}
                      {request.paid_days != null && request.paid_days < Math.round(request.hours / 10) && (
                        <span style={{ color: '#f59e0b' }}>
                          {' '}({request.paid_days} paid, {Math.round(request.hours / 10) - request.paid_days} unpaid)
                        </span>
                      )}
                      {' • '}
                      {formatDate(request.start_date)}
                      {request.start_date !== request.end_date && ` - ${formatDate(request.end_date)}`}
                      {' • '}{request.hours} hours
                    </div>
                    {request.notes && (
                      <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                        {request.notes}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleApprove(request.id)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#22c55e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: '500'
                      }}
                    >
                      <FiCheck /> Approve
                    </button>
                    <button
                      onClick={() => handleDeny(request.id)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: '500'
                      }}
                    >
                      <FiX /> Deny
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* All Requests History */}
          <h2 style={{ fontSize: '18px', margin: '30px 0 15px', color: '#333' }}>
            All Requests
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {requests.filter(r => r.status !== 'Pending').map(request => (
              <div
                key={request.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '16px 20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  opacity: 0.8
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', color: '#333' }}>
                    {request.team_member_name}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    {request.request_type}
                    {request.paid_days != null && request.paid_days < Math.round(request.hours / 10) && (
                      <span style={{ color: '#f59e0b' }}>
                        {' '}({request.paid_days} paid, {Math.round(request.hours / 10) - request.paid_days} unpaid)
                      </span>
                    )}
                    {' • '}
                    {formatDate(request.start_date)}
                    {request.start_date !== request.end_date && ` - ${formatDate(request.end_date)}`}
                    {' • '}{request.hours} hours
                  </div>
                </div>
                <span style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: `${getStatusColor(request.status)}15`,
                  color: getStatusColor(request.status)
                }}>
                  {request.status}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* All Employees Tab (Managers) */}
      {activeTab === 'all-balances' && isManager && (
        <>
          <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>
            Employee Time Off Balances
          </h2>
          
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600' }}>Employee</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600' }}>Hire Date</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#2563eb' }}>Vacation</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#22c55e' }}>Sick</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#8b5cf6' }}>Personal</th>
                </tr>
              </thead>
              <tbody>
                {allBalances.map((emp, idx) => (
                  <tr key={emp.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: '500' }}>{emp.name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{emp.balances.employment_type}</div>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {formatDate(emp.balances.hire_date)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{ fontWeight: '600', color: '#2563eb' }}>{emp.balances.vacation.available}</span>
                      <span style={{ color: '#999' }}> / {emp.balances.vacation.entitled}</span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{ fontWeight: '600', color: '#22c55e' }}>{emp.balances.sick.available}</span>
                      <span style={{ color: '#999' }}> / {emp.balances.sick.total}</span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{ fontWeight: '600', color: '#8b5cf6' }}>{emp.balances.personal.available}</span>
                      <span style={{ color: '#999' }}> / {emp.balances.personal.entitled}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Request Form Modal */}
      {showRequestForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '450px',
            maxHeight: 'calc(90dvh - 90px)',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FiCalendar /> Request Time Off
            </h2>
            
            <form onSubmit={handleSubmitRequest}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                  Type
                </label>
                <select
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '14px'
                  }}
                >
                  <option value="Vacation">Vacation</option>
                  <option value="Sick">Sick Leave</option>
                  <option value="Personal">Personal Leave</option>
                  <option value="Unpaid">Unpaid</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                  Total Hours
                </label>
                <input
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  required
                  min="1"
                  step="0.5"
                  placeholder="Auto-calculated based on dates"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '14px'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  10 hours = 1 work day (Mon-Thu only)
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Reason for request..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Paid Days Selector */}
              {requestType !== 'Unpaid' && workdays > 0 && balances && (
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '14px', marginBottom: '10px' }}>
                    <strong>Available {requestType}:</strong>{' '}
                    {requestType === 'Vacation' && `${balances.vacation.available} days`}
                    {requestType === 'Sick' && `${balances.sick.available} days`}
                    {requestType === 'Personal' && `${balances.personal.available} days`}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                      Paid days to use:
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={Math.min(workdays, Math.floor(balances[requestType.toLowerCase()]?.available || 0))}
                      value={paidDays ?? workdays}
                      onChange={(e) => setPaidDays(Math.max(0, Math.min(workdays, parseInt(e.target.value) || 0)))}
                      style={{
                        width: '80px',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        fontSize: '14px',
                        textAlign: 'center'
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#666', marginLeft: '8px' }}>
                      of {workdays} work day{workdays !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {(paidDays ?? workdays) < workdays && (
                    <div style={{
                      fontSize: '13px',
                      color: '#f59e0b',
                      backgroundColor: '#fffbeb',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      marginTop: '8px'
                    }}>
                      {workdays - (paidDays ?? workdays)} day{workdays - (paidDays ?? workdays) !== 1 ? 's' : ''} will be unpaid
                    </div>
                  )}
                </div>
              )}
              {requestType === 'Unpaid' && workdays > 0 && (
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px',
                  color: '#666'
                }}>
                  All {workdays} day{workdays !== 1 ? 's' : ''} will be unpaid
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowRequestForm(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: submitting ? 0.7 : 1
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimeOff;